import { useCallback, useEffect, useState } from 'react'

import { type TermsMethodsKindEnum } from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import { useDeleteTermOrMethodPresenter } from '@webapp/modules/purchasing/features/delete-term-or-method/presenters/UseDeleteTermOrMethodPresenter'
import { type TermOrMethod } from '@webapp/modules/purchasing/features/list-terms-and-methods/models'

/**
 * Orchestration hook for POTermsMethodsView.
 *
 * Owns the create-modal lifecycle (BHV-028) and the cross-feature
 * CREATED/UPDATED indicator state (BHV-029/030) — raised by the create/edit
 * features via onSuccess(id), displayed by the list feature. Row deletes are
 * delegated to the hook-only delete command (BHV-031).
 */
export const usePOTermsMethodsView = (kind: TermsMethodsKindEnum) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [justCreatedId, setJustCreatedId] = useState<number | null>(null)
  const [justUpdatedId, setJustUpdatedId] = useState<number | null>(null)

  // Indicators are per-kind — clear them if the tab switches kind without
  // remounting the Feature Set
  useEffect(() => {
    setJustCreatedId(null)
    setJustUpdatedId(null)
    setIsCreateModalOpen(false)
  }, [kind])

  const {
    commands: { deleteItem },
  } = useDeleteTermOrMethodPresenter(kind)

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  const handleCreateSuccess = useCallback((id: number) => {
    setJustCreatedId(id)
    setIsCreateModalOpen(false)
  }, [])

  const handleEditSuccess = useCallback((id: number) => {
    setJustUpdatedId(id)
  }, [])

  const onDeleteItem = (item: TermOrMethod) => {
    deleteItem(item)
  }

  return {
    states: {
      isCreateModalOpen,
      justCreatedId,
      justUpdatedId,
    },
    methods: {
      openCreateModal,
      closeCreateModal,
      handleCreateSuccess,
      handleEditSuccess,
      onDeleteItem,
    },
  }
}
